import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, X, Send, Loader2, ChevronDown } from 'lucide-react';
import MicButton from './ui/MicButton';
import { QuoteCard, DraftLink } from './CopilotQuoteCard';
import { api } from '../lib/api';
import { NO_ACTIVE, nextActive, chatBody, appliesTo, clearActive, afterFailure, askAction } from '../../modules/copilot/panel-state.mjs';

export const COPILOT_SUGGESTIONS = [
  "What's overdue?",
  "What's open with Concria?",
  'Who owes us and what do we owe?',
  'What bids are due?',
  'Status of Barton Malow',
  'Draft a quote for 5,000 sf grey',
];

/** True when the panel sent the question or placed it in its input; false when the caller should keep it. */
export function askCopilot(text) {
  const detail = { text, action: '' };
  window.dispatchEvent(new CustomEvent('copilot:ask', { detail }));
  return detail.action === 'send' || detail.action === 'place';
}

const DOCKED_ROUTE = /^\/quotes\/[0-9a-f-]{36}\/?$/i;

// Turn portal paths in an answer ("/invoices/<id>", "(open: /quotes/<id>)") into links.
const PATH_RE = /(\/(?:leads|accounts|contacts|projects|tasks|bids|quotes|catalog|concria-meet|orders|shipments|invoices|books|concria-oy|investments|marketing)(?:\/[0-9a-f-]{36})?)(?![\w/-])/g;
export function Linkified({ text, onNavigate }) {
  const parts = String(text || '').split(PATH_RE);
  return parts.map((p, i) => (i % 2 === 1
    ? <Link key={i} to={p} onClick={onNavigate} className="font-medium text-primary underline underline-offset-2">{p.split('/').length > 2 ? 'open' : p.slice(1).replace(/-/g, ' ')}</Link>
    : <span key={i}>{p}</span>));
}

/**
 * CopilotPanel: floating assistant over live portal data. Reads every module the user can
 * read; the only actions are drafts (a quote and its revisions, the firm version, the
 * ready-for-approval mark, a supplier RFQ to Concria Oy and its changes, a task, content), never
 * sent or approved. After a quote action the reply carries an inline quote card with the internal
 * basis for pricing; follow-up edits ("make the shake $1.70") apply to the last quote card shown.
 * After an RFQ is made or changed, follow-up edits ("use 1.8 lb/sf") apply to that RFQ, never to
 * the quote (the panel sends active_rfq_id; modules/copilot/panel-state.mjs).
 * Full screen on phones, a side panel on larger screens.
 */
export default function CopilotPanel() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // The quote card and the RFQ last shown: edits without a number apply to the one in front.
  const [active, setActive] = useState(NO_ACTIVE);
  const activeLine = appliesTo(active);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const askRef = useRef(null);
  // LP-402: the quote builder (/quotes/:id) docks its own Copilot (CopilotDock), so this one stands down there.
  const docked = DOCKED_ROUTE.test(useLocation().pathname);
  const dockedRef = useRef(docked);
  dockedRef.current = docked;

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const onAsk = (e) => { if (dockedRef.current) return; setOpen(true); if (e.detail?.text) e.detail.action = askRef.current?.(e.detail.text) || ''; };
    window.addEventListener('copilot:ask', onAsk);
    return () => window.removeEventListener('copilot:ask', onAsk);
  }, []);

  async function sendMessage(textArg) {
    const text = String(textArg ?? input).trim();
    if (!text || loading) return;
    // Clear the input only when it is what was sent.
    if (textArg == null) setInput('');
    const userMsg = { role: 'user', content: text };
    const history = messages.slice(-8).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);
    const sent = active;
    try {
      const res = await api.post('/copilot/chat', chatBody(text, history, active));
      const drafts = res.drafts || [];
      setActive((prev) => nextActive(prev, drafts));
      setMessages((prev) => [...prev, { role: 'assistant', content: res.answer || "I couldn't find an answer.", toolsUsed: res.toolsUsed || [], drafts }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: e.message && /not configured|partners only/i.test(e.message) ? e.message : 'Something went wrong. Please try again.', toolsUsed: [] }]);
      // The answer was lost (network, proxy timeout, error answer): keep the RFQ in front, and let the
      // quote go after a lost request for an RFQ (panel-state.mjs afterFailure). A "clear" meanwhile wins.
      setActive((prev) => (prev === sent ? afterFailure(sent, text) : prev));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  // Take a question from outside the panel without overwriting or dropping a draft.
  function takeAsk(question) {
    const action = askAction(question, { loading, input });
    if (action === 'send') sendMessage(question);
    else if (action === 'place') { setInput(String(question).trim()); inputRef.current?.focus(); }
    return action;
  }
  askRef.current = takeAsk;

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const closeOnPhone = () => { if (window.innerWidth < 640) setOpen(false); };
  if (docked) return null;

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-slate-800 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 sm:bottom-6 sm:right-6 ${open ? 'hidden sm:flex' : ''}`}
        title="Portal Copilot"
      >
        {open ? <ChevronDown size={16} /> : <Bot size={16} />}
        {open ? 'Close' : 'Copilot'}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-900 sm:inset-auto sm:bottom-20 sm:right-6 sm:max-h-[75vh] sm:w-[26rem] sm:max-w-[calc(100vw-3rem)] sm:rounded-xl sm:border sm:border-gray-200 sm:shadow-2xl sm:dark:border-slate-700">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-slate-700">
            <div className="flex min-w-0 items-center gap-2">
              <Bot size={18} className="shrink-0 text-primary" />
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Portal Copilot</span>
              <span className="truncate text-xs text-gray-400">live data, drafts only</span>
            </div>
            <button onClick={() => setOpen(false)} className="rounded-lg p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close Copilot">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <p className="mb-3">Ask about anything in the portal: quotes, orders, shipments, invoices, Concria Oy, bids, deals and payouts, tasks. I can also start a draft quote, a task or a content draft for you to review. I never send or approve anything.</p>
                <div className="flex flex-wrap gap-2">
                  {COPILOT_SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => sendMessage(s)} className="rounded-full border border-gray-200 px-3 py-1.5 text-xs hover:border-primary hover:text-primary dark:border-slate-600">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] break-words rounded-xl px-3 py-2 text-sm ${msg.role === 'user' ? 'rounded-br-none bg-primary text-white' : 'rounded-bl-none bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-200'}`}>
                  <span className="whitespace-pre-wrap">{msg.role === 'assistant' ? <Linkified text={msg.content} onNavigate={closeOnPhone} /> : msg.content}</span>
                  {msg.drafts?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {msg.drafts.map((d) => {
                        if (d.kind === 'quote' && d.quote) return <QuoteCard key={`q-${d.id}`} q={d.quote} onNavigate={closeOnPhone} />;
                        // A link only when its href stays on this origin (LP-443); else the label as text.
                        return <DraftLink key={d.id} d={d} onNavigate={closeOnPhone} />;
                      })}
                    </div>
                  )}
                  {msg.toolsUsed?.length > 0 && (
                    <div className="mt-1.5 text-[11px] opacity-50">
                      from {[...new Set(msg.toolsUsed.map((t) => t.name.replace(/_/g, ' ')))].join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-xl rounded-bl-none bg-gray-100 px-3 py-2 text-xs text-gray-500 dark:bg-slate-800">
                  <Loader2 size={14} className="animate-spin" /> Looking it up
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {activeLine && (
            <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 pt-2 text-[11px] text-gray-500 dark:border-slate-700 dark:text-gray-400">
              <span className="truncate">{activeLine}</span>
              <button type="button" onClick={() => setActive(clearActive)} className="shrink-0 underline underline-offset-2 hover:text-primary">clear</button>
            </div>
          )}
          <div className="flex items-end gap-2 border-t border-gray-100 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-slate-700">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your business"
              className="max-h-24 min-w-0 flex-1 resize-none overflow-y-auto rounded-lg border border-gray-300 bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary/50 dark:border-slate-600 dark:bg-slate-800 sm:text-sm"
              style={{ minHeight: '40px' }}
            />
            <MicButton onTranscript={(t) => setInput((p) => (p ? `${p} ${t}` : t).trim())} size={16} />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              className="rounded-lg bg-primary p-2.5 text-white transition-colors hover:bg-primary/90 disabled:opacity-40"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
